
import { Box, Button, Stack, Typography } from "@mui/material";

import type { WidgetProps } from "./types";

// Card grid for multiple PDF documents. Each card shows the PDF's first page
// rendered in a small, non-interactive iframe, plus title/description/CTA.
//
// Why an iframe for the thumbnail: avoids server-side PDF rasterization deps
// (poppler / ghostscript / canvas). Browsers render the first page natively;
// pointer-events:none + overflow:hidden + #toolbar=0 give us a static cover.
// If thumbnailUrl is supplied (pre-rendered PNG), we use that instead.
export function PDFGrid({ widget }: WidgetProps<"pdf_grid">) {
    const { title, description, items, columns } = widget.props;
    const cols = columns ?? Math.min(items.length, 4);

    return (
        <Stack spacing={2}>
            {title && <Typography variant="h5">{title}</Typography>}
            {description && (
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            )}
            <Box
                sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: `repeat(${Math.min(cols, 3)}, 1fr)`,
                        lg: `repeat(${cols}, 1fr)`,
                    },
                }}
            >
                {items.map((item, idx) => (
                    <PDFCard key={`${widget.id}-${idx}`} item={item} />
                ))}
            </Box>
        </Stack>
    );
}

type Item = WidgetProps<"pdf_grid">["widget"]["props"]["items"][number];

function PDFCard({ item }: { item: Item }) {
    const { title, description, url, thumbnailUrl } = item;
    // 8.5:11 aspect — matches US Letter cover. Capped to keep the grid compact.
    const aspectRatio = "8.5 / 11";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                border: 1,
                borderColor: "divider",
                borderRadius: "10px",
                bgcolor: "background.paper",
                overflow: "hidden",
            }}
        >
            <Box
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${title}`}
                sx={{
                    position: "relative",
                    display: "block",
                    width: "100%",
                    aspectRatio,
                    bgcolor: "grey.100",
                    overflow: "hidden",
                    cursor: "pointer",
                    "&:hover .pdf-card-overlay": { opacity: 1 },
                }}
            >
                {thumbnailUrl ? (
                    <Box
                        component="img"
                        src={thumbnailUrl}
                        alt={`${title} cover`}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                ) : (
                    <Box
                        component="iframe"
                        src={`${url}#view=FitH&toolbar=0&navpanes=0&scrollbar=0&zoom=page-fit&page=1`}
                        title={`${title} preview`}
                        sx={{
                            width: "100%",
                            height: "100%",
                            border: 0,
                            display: "block",
                            // Static cover — clicks bubble to the parent anchor.
                            pointerEvents: "none",
                        }}
                    />
                )}
                <Box
                    className="pdf-card-overlay"
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(15, 28, 51, 0.55)",
                        color: "common.white",
                        fontWeight: 600,
                        fontSize: 14,
                        opacity: 0,
                        transition: "opacity 150ms ease-out",
                    }}
                >
                    Open PDF
                </Box>
            </Box>
            <Stack spacing={1} sx={{ p: 2, flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                        {description}
                    </Typography>
                )}
                <Box>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        View PDF
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
}
